import { useContext, useEffect, useMemo, useState } from 'react';

import { Box, Stack, TextField, Typography } from '@mui/material';

import { LayerContext } from 'state/LayerContext';
import { useGetSegmentInfoQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectRouteSelection,
  selectSegmentId,
  selectWorkflow,
} from 'state/workflowSlice';

import { WorkflowType } from 'appConstants';
import { MapContext } from 'features/map/MapContext';
import { useSpeedsExportData } from 'features/workflow_nrt/useSpeedsExportData';
import { useVolumeExportData } from 'features/workflow_planning/useVolumeExportData';
import { useQueueExportData } from 'features/workflow_queuing/useQueueExportData';
import { useTravelTimesExportData } from 'features/workflow_traveltime/useTravelTimesExportData';

import { SuccessAlert } from './Alerts';
import { StlButton } from './StlButton/StlButton';
import { StlPopup } from './StlPopup/StlPopup';
import { ToggleGroup } from './ToggleGroup';
import {
  ExportType,
  downloadChartScreenshot,
  downloadMapScreenshot,
  useExportFilename,
} from './exportUtils';
import { routeSelectionIsSavedRoute, useRouteInfo } from './hooks/useRouteInfo';
import { showToast } from './toasts/ToastProvider';
import { classNames } from './utils';

import styles from './ExportPopup.module.css';

interface IExportPopup {
  open: boolean;
  setOpen: (v: boolean) => void;
}

const WORKFLOW_TO_EXPORT_TYPES: Partial<Record<WorkflowType, ExportType[]>> = {
  [WorkflowType.Plan]: [ExportType.Map, ExportType.Chart, ExportType.Data],
  [WorkflowType.TravelTime]: [
    ExportType.Map,
    ExportType.Chart,
    ExportType.Data,
  ],
  [WorkflowType.Speed]: [ExportType.Map, ExportType.Chart, ExportType.Data],
  [WorkflowType.Queuing]: [ExportType.Map, ExportType.Chart, ExportType.Data],
};

const EXPORT_TYPE_TO_FORMAT: Record<ExportType, string> = {
  [ExportType.Map]: '.png',
  [ExportType.Chart]: '.png',
  [ExportType.Data]: '.csv',
};

function getDefaultExportType(workflow: WorkflowType): ExportType {
  return WORKFLOW_TO_EXPORT_TYPES[workflow]?.[0] ?? ExportType.Map;
}

function useWorkflowExportData(): {
  handleExport: () => void;
  isActive: boolean;
} {
  const workflow = useAppSelector(selectWorkflow);

  const speeds = useSpeedsExportData(workflow !== WorkflowType.Speed);
  const volume = useVolumeExportData(workflow !== WorkflowType.Plan);
  const travelTimes = useTravelTimesExportData(
    workflow !== WorkflowType.TravelTime,
  );
  const queue = useQueueExportData(workflow !== WorkflowType.Queuing);

  switch (workflow) {
    case WorkflowType.Speed:
      return speeds;
    case WorkflowType.Plan:
      return volume;
    case WorkflowType.TravelTime:
      return travelTimes;
    case WorkflowType.Queuing:
      return queue;
    default:
      return { handleExport: () => {}, isActive: false };
  }
}

export function ExportPopup({ open, setOpen }: IExportPopup) {
  const workflow = useAppSelector(selectWorkflow);
  const routeSelection = useAppSelector(selectRouteSelection);
  const segmentId = useAppSelector(selectSegmentId);

  const isSavedRoute = routeSelectionIsSavedRoute(routeSelection);
  const { layer } = useContext(LayerContext);
  const { savedRoute } = useRouteInfo(!isSavedRoute);
  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: [segmentId] },
    { skip: !!savedRoute || !layer || !segmentId },
  );

  const [exportType, setExportType] = useState<ExportType>(
    WORKFLOW_TO_EXPORT_TYPES[workflow][0],
  );
  const { handleExport, isActive } = useWorkflowExportData();
  const filename = useExportFilename(exportType);
  const { refMap } = useContext(MapContext);

  useEffect(() => {
    setExportType(getDefaultExportType(workflow));
  }, [workflow]);

  const handleExportToggleClick = (event) => {
    setExportType(event.target.value);
  };

  const handleApply = () => {
    switch (exportType) {
      case ExportType.Map:
        return downloadMapScreenshot(refMap?.current, filename);
      case ExportType.Chart:
        if (!segmentId)
          return showToast(
            'warning',
            'Please select a segment to generate the chart',
          );
        else return downloadChartScreenshot(filename);
      case ExportType.Data:
        return handleExport();
      default:
        return;
    }
  };

  const isDisabled = exportType === ExportType.Data && !isActive;

  const title = useMemo(() => {
    let extra = '';
    if (routeSelection && savedRoute) extra = savedRoute.routeName;
    else if (segmentData)
      extra = segmentData?.segments[segmentId]?.properties?.name;
    return `Export ${extra}`;
  }, [savedRoute, segmentData, segmentId, routeSelection]);

  return (
    <StlPopup
      title={title}
      isOpen={open}
      onClose={() => setOpen(false)}
      onApply={handleApply}
      btnTxt="Export"
      sx={{ minWidth: '30rem' }}
      disabled={isDisabled}
      className={styles.exportPopup}
    >
      <Box className={styles.content}>
        <Stack className={styles.export}>
          <Box display="flex" alignItems="center">
            <Typography className={styles.text} variant="body2">
              File
            </Typography>
            <ToggleGroup
              horizontal
              value={exportType}
              values={WORKFLOW_TO_EXPORT_TYPES[workflow]}
              handleClick={handleExportToggleClick}
              className={styles.toggleGroup}
            />
          </Box>
          <Box display="flex">
            <Typography className={styles.text} variant="body2">
              Format
            </Typography>
            <Typography
              className={classNames(styles.text, styles.dimmed)}
              variant="body2"
            >
              {EXPORT_TYPE_TO_FORMAT[exportType]}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </StlPopup>
  );
}

export function SharePopup({ open, setOpen }: IExportPopup) {
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  // remove copy info after 4 seconds
  useEffect(() => {
    if (!copied) return;

    const timeout = window.setTimeout(() => {
      setCopied(false);
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [copied]);

  const url = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = async () => {
    try {
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <StlPopup
      title="Share"
      isOpen={open}
      onClose={() => setOpen(false)}
      btnTxt="Export"
      sx={{ minWidth: '30rem' }}
      cancel={false}
      apply={false}
      className={styles.sharePopup}
    >
      <Typography variant="body2" className={styles.subtitle}>
        Recipients of this link will see the same view and settings
      </Typography>
      <Box display="flex" gap={0.5}>
        <TextField
          className={styles.urlText}
          variant="outlined"
          fullWidth
          value={url}
          size="small"
          margin="dense"
        />
        <StlButton
          width={81}
          value="Copy link"
          handleClick={copyLink}
          typographyVariant="h4"
          brown
        />
      </Box>
      {copied && <SuccessAlert txt="Link copied successfully" />}
    </StlPopup>
  );
}
